<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from "vue-i18n";
import { filterListType } from "@/assets/ts/types/filters";
import CatalogItem from "@/components/catalog/catalogItem.vue";
import CatalogBanner from "@/components/catalog/catalogBanner.vue";
import CatalogItemSkeleton from "@/components/catalog/catalogItemSkeleton.vue";
import CatalogResultEmpty from "@/components/catalog/catalogResultEmpty.vue";
import {Config} from "@/assets/ts/config";

interface Product {
  set_id: string;
  hash?: string;
  price: number;
  price_gold: number;
  price_platinum: number;
  metal: string;
  stones: string;
  image: {
    filename: string
    filename_layer: string
    positions: {
      filename: string
      filename_layer: string
    }[]
  };
  order_idx?: number;
  link: string
}

interface CatalogResponse {
  request: {
    max: number;
    [key: string]: any;
  };
  filter: filterListType;
  images: string[];
  response: Product[];
}

interface IndividualItemResponse {
  request: {
    catalog_id: string;
    idx: number;
    max: number;
    parameter: object;
  };
  response: Product & {
    order_idx: number;
    status: string;
  };
}

interface MultipleItemsResponse {
  request: {
    catalog_id: string;
    idx: number[];
    max: number;
    parameter: object;
  };
  response: (Product & {
    order_idx: number;
    status: string;
  })[];
}

const props = defineProps<{
  catalogBrand: string;
  catalogItemType: string;
  activeFilters: { [key: string]: string };
  country: string;
  language: string;
  fixImagePosition: string;
  hash: string;
}>();

const emit = defineEmits<{
  (e: 'update:products', value: Product[]): void;
  (e: 'update:failedProducts', value: Product[]): void;
  (e: 'update:filters', value: filterListType): void;
  (e: 'update:possibleImagePositions', value: string[]): void;
  (e: 'update:loading', value: boolean): void;
  (e: 'update:error', value: string | null): void;
  (e: 'update-filter', filterName: string, value: string): void;
}>();

const { t } = useI18n();
const pageStr = "components.catalog"

const products = ref<Product[]>([]);
const failedProducts = ref<Product[]>([]);
watch(failedProducts, (val) => {
  emit('update:failedProducts', val);
});
const filters = ref<filterListType>({});
const possibleImagePositions = ref<string[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
const isLoadingMore = ref(false);
const error = ref<string | null>(null);
const usedHashes = new Set<string>();
const currentIndex = ref(0);
const totalMaxItems = ref(0);
const bufferedItems = ref<Product[]>([]);
const lastFetchedParams = ref('');
const itemsAddedInCurrentBatch = ref(0);

const failedProductsExpanded = ref(false);
const labProducts = ref<Product[]>([]);
const labTotalMaxItems = ref(0);
const labCurrentIndex = ref(0);
const labBufferedItems = ref<Product[]>([]);
const labLoadingMore = ref(false);
const isLoadingMoreLab = ref(false);
const labItemsAddedInCurrentBatch = ref(0);

const maxLoadMoreItems = 16;
const maxConcurrentRequests = 16;
const requestTimeoutMs = 10000;
// Number of idxs fetched per HTTP request. When set to 1, items are fetched one by one
// via fetchIndividualItem (the original process). When > 1, idxs are grouped and fetched
// together via fetchMultipleItems, which hits a single endpoint returning a list of items.
const idxBatchSize = 4;

// Aborts all in-flight requests belonging to the previous "generation" whenever
// a brand new fetchProducts() call supersedes them (e.g. filters changed mid-flight).
let activeAbortController = new AbortController();
// De-duplicates concurrent/duplicate fetchIndividualItem calls for the same idx/filters combo.
const individualItemCache = new Map<string, Promise<IndividualItemResponse | null>>();
// De-duplicates concurrent/duplicate fetchMultipleItems calls for the same idxs/filters combo.
const multipleItemsCache = new Map<string, Promise<MultipleItemsResponse['response'] | null>>();

const isPriceFilterActive = computed(() => {
  return !!props.activeFilters.price
})

const isMetalFilterActive = computed(() => {
  return !!props.activeFilters.metal
})

const isWeddingCatalog = computed(() => {
  return props.catalogItemType.includes('wedding')
})

// For wedding ring catalogs, the "min-price" sort is always applied
// automatically as long as no price or metal filter is active.
const isMinPriceAllowed = computed(() => {
  return isWeddingCatalog.value && !isPriceFilterActive.value && !isMetalFilterActive.value
})

const showLabProducts = computed(() => {
  return isPriceFilterActive.value && props.activeFilters.stone_type !== 'lab'
})

const sortProducts = (products: Product[]) => {
  return products.sort((a, b) => {
    if (isPriceFilterActive.value) {
      return (a.price ?? 0) - (b.price ?? 0);
    }
    return (a.order_idx ?? 0) - (b.order_idx ?? 0);
  });
};

const getQueryParams = () => {
  const queryParams = new URLSearchParams();
  Object.entries(props.activeFilters)
      .filter(([key, value]) => !(key === 'stone_type' && value === 'earth'))
      .forEach(([key, value]) => queryParams.append(key, value));

  return queryParams;
}

const fetchIndividualItem = async (idx: number, baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<IndividualItemResponse | null> => {
  if (signal?.aborted) {
    return null;
  }

  const cacheKey = `${idx}-${stoneTypeOverride ?? ''}-${baseQueryParams.toString()}`;
  const cached = individualItemCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const queryParams = new URLSearchParams(baseQueryParams);
  if (stoneTypeOverride) {
    queryParams.set('stone_type', stoneTypeOverride);
  }
  const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}/${idx}?${queryParams.toString()}`;

  const requestPromise = (async (): Promise<IndividualItemResponse | null> => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), requestTimeoutMs);
    const onExternalAbort = () => timeoutController.abort();
    signal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, { signal: timeoutController.signal });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (e) {
      if (!signal?.aborted) {
        console.error(`Failed to fetch item ${idx}`, e);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  })();

  individualItemCache.set(cacheKey, requestPromise);
  requestPromise.then((result) => {
    // Only cache successful lookups; allow failed/aborted/timed-out requests to be retried later.
    if (result === null) {
      individualItemCache.delete(cacheKey);
    }
  });

  return requestPromise;
};

// Fetches multiple idxs in a single request. The idxs are joined by "-" in the URL and the
// response is a list of items (instead of the single object returned by fetchIndividualItem).
const fetchMultipleItems = async (idxs: number[], baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<MultipleItemsResponse['response'] | null> => {
  if (signal?.aborted || idxs.length === 0) {
    return null;
  }

  const idxKey = idxs.join('-');
  const cacheKey = `${idxKey}-${stoneTypeOverride ?? ''}-${baseQueryParams.toString()}`;
  const cached = multipleItemsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const queryParams = new URLSearchParams(baseQueryParams);
  if (stoneTypeOverride) {
    queryParams.set('stone_type', stoneTypeOverride);
  }
  const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}/${idxKey}?${queryParams.toString()}`;

  const requestPromise = (async (): Promise<MultipleItemsResponse[] | null> => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), requestTimeoutMs);
    const onExternalAbort = () => timeoutController.abort();
    signal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, { signal: timeoutController.signal });
      if (!response.ok) {
        return null;
      }
      const data: MultipleItemsResponse[] = await response.json();

      return data.map(item => item.response);
    } catch (e) {
      if (!signal?.aborted) {
        console.error(`Failed to fetch items ${idxKey}`, e);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  })();

  multipleItemsCache.set(cacheKey, requestPromise);
  requestPromise.then((result) => {
    // Only cache successful lookups; allow failed/aborted/timed-out requests to be retried later.
    if (result === null) {
      multipleItemsCache.delete(cacheKey);
    }
  });

  return requestPromise;
};

// Fetches a chunk of idxs, transparently using fetchIndividualItem when the chunk holds a single
// idx (idxBatchSize === 1, the original process) or fetchMultipleItems for larger chunks.
const fetchIndicesChunk = async (idxs: number[], baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<MultipleItemsResponse['response']> => {
  if (idxs.length === 1) {
    const data = await fetchIndividualItem(idxs[0], baseQueryParams, stoneTypeOverride, signal);
    return data && data.response ? [data.response] : [];
  }
  const items = await fetchMultipleItems(idxs, baseQueryParams, stoneTypeOverride, signal);
  return items ?? [];
};

const fetchItemsBatch = async (targetCount: number, context: 'main' | 'lab' = 'main') => {
  const isLab = context === 'lab';
  const productsRef = isLab ? labProducts : products;
  const currentIndexRef = isLab ? labCurrentIndex : currentIndex;
  const totalMaxItemsRef = isLab ? labTotalMaxItems : totalMaxItems;
  const bufferedItemsRef = isLab ? labBufferedItems : bufferedItems;
  const isLoadingMoreRef = isLab ? isLoadingMoreLab : isLoadingMore;
  const loadingMoreRef = isLab ? labLoadingMore : loadingMore;
  const itemsAddedInCurrentBatchRef = isLab ? labItemsAddedInCurrentBatch : itemsAddedInCurrentBatch;
  const stoneTypeOverride = isLab ? 'lab' : undefined;
  const baseQueryParams = getQueryParams();
  const signal = activeAbortController.signal;

  itemsAddedInCurrentBatchRef.value = 0;

  try {
    const mainOrderIndices = new Set(products.value.map(p => p.order_idx));

    const flushItems = (itemsToFlush: Product[]) => {
      if (itemsToFlush.length === 0) return;

      let itemsToActuallyAdd: Product[] = [];
      let itemsToBuffer: Product[] = [];

      for (const item of itemsToFlush) {
        if (itemsAddedInCurrentBatchRef.value + itemsToActuallyAdd.length < targetCount) {
          itemsToActuallyAdd.push(item);
        } else {
          itemsToBuffer.push(item);
        }
      }

      if (itemsToActuallyAdd.length > 0) {
        const existingIds = new Set(productsRef.value.map(p => p.set_id));
        const uniqueItems = itemsToActuallyAdd.filter(item => !existingIds.has(item.set_id));

        if (uniqueItems.length > 0) {
          productsRef.value = sortProducts([...productsRef.value, ...uniqueItems]);
          itemsAddedInCurrentBatchRef.value += uniqueItems.length;

          if (!isLab) {
            if (loading.value) {
              loading.value = false;
              emit('update:loading', false);
            }
            emit('update:products', products.value);

            const newlyAddedIndices = new Set(uniqueItems.map(p => p.order_idx));
            labProducts.value = labProducts.value.filter(p => !newlyAddedIndices.has(p.order_idx));
            labBufferedItems.value = labBufferedItems.value.filter(p => !newlyAddedIndices.has(p.order_idx));
          }
        }
      }

      if (itemsToBuffer.length > 0) {
        bufferedItemsRef.value.push(...itemsToBuffer);
      }
    };

    // Pull from buffer first
    let itemsFromBuffer: Product[] = [];
    while (bufferedItemsRef.value.length > 0 && itemsFromBuffer.length < targetCount) {
      const item = bufferedItemsRef.value.shift()!;
      if (isLab) {
        if (!mainOrderIndices.has(item.order_idx)) {
          itemsFromBuffer.push(item);
          failedProducts.value = failedProducts.value.filter(p => p.order_idx !== item.order_idx);
        }
      } else {
        itemsFromBuffer.push(item);
        labProducts.value = labProducts.value.filter(p => p.order_idx !== item.order_idx);
        labBufferedItems.value = labBufferedItems.value.filter(p => p.order_idx !== item.order_idx);
      }
    }
    flushItems(itemsFromBuffer);

    if (itemsAddedInCurrentBatchRef.value < targetCount && currentIndexRef.value < totalMaxItemsRef.value) {
      isLoadingMoreRef.value = true;
      let successfulInFetch = 0;
      const neededFromFetch = targetCount - itemsAddedInCurrentBatchRef.value;
      const activeRequests = new Set<Promise<void>>();
      const fetchedItems: Product[] = [];
      const fetchedFailedItems: Product[] = [];
      let lastFlushedCount = 0;

      while (successfulInFetch < neededFromFetch && currentIndexRef.value < totalMaxItemsRef.value) {
        const remainingNeeded = neededFromFetch - successfulInFetch;
        const maxActiveRequests = Math.min(maxConcurrentRequests, Math.ceil(remainingNeeded / idxBatchSize));
        if (activeRequests.size < maxActiveRequests && currentIndexRef.value < totalMaxItemsRef.value) {
          const chunkIdxs: number[] = [];
          while (chunkIdxs.length < idxBatchSize && currentIndexRef.value < totalMaxItemsRef.value) {
            chunkIdxs.push(currentIndexRef.value++);
          }
          const promise = (async () => {
            const items = await fetchIndicesChunk(chunkIdxs, baseQueryParams, stoneTypeOverride, signal);
            for (const responseItem of items) {
              const item = {
                ...responseItem,
                order_idx: responseItem.order_idx
              };
              if (responseItem.status !== 'failed') {
                if (!item.hash || !usedHashes.has(item.hash)) {
                  if (item.hash) usedHashes.add(item.hash);

                  if (isLab) {
                    if (!mainOrderIndices.has(item.order_idx)) {
                      fetchedItems.push(item);
                      successfulInFetch++;
                      failedProducts.value = failedProducts.value.filter(p => p.order_idx !== item.order_idx);
                    }
                  } else {
                    fetchedItems.push(item);
                    successfulInFetch++;
                    labProducts.value = labProducts.value.filter(p => p.order_idx !== item.order_idx);
                    labBufferedItems.value = labBufferedItems.value.filter(p => p.order_idx !== item.order_idx);
                  }
                }
              } else {
                fetchedFailedItems.push(item);
              }
            }
            activeRequests.delete(promise);
          })();
          activeRequests.add(promise);
        } else if (activeRequests.size > 0) {
          await Promise.race(activeRequests);

          const newlyFetchedCount = fetchedItems.length - lastFlushedCount;
          if (newlyFetchedCount >= 4) {
            const itemsToFlush = sortProducts(fetchedItems.slice(lastFlushedCount));
            flushItems(itemsToFlush);
            lastFlushedCount = fetchedItems.length;
          }
        } else {
          break;
        }
      }

      await Promise.all(activeRequests);

      if (lastFlushedCount < fetchedItems.length) {
        const itemsToFlush = sortProducts(fetchedItems.slice(lastFlushedCount));
        flushItems(itemsToFlush);
      }

      if (!isLab) {
        failedProducts.value = sortProducts([...failedProducts.value, ...fetchedFailedItems]);
      }
    }

    const finalMainIndices = new Set(products.value.map(p => p.order_idx));
    labProducts.value = labProducts.value.filter(p => !finalMainIndices.has(p.order_idx));
    labBufferedItems.value = labBufferedItems.value.filter(p => !finalMainIndices.has(p.order_idx));

    loadingMoreRef.value = currentIndexRef.value < totalMaxItemsRef.value || bufferedItemsRef.value.length > 0;
  } catch (e) {
    console.error(e)
    if (!isLab) {
      error.value = t(`${pageStr}.load_failed`);
      emit('update:error', error.value);
    }
  } finally {
    isLoadingMoreRef.value = false;
    if (!isLab) {
      emit('update:products', products.value);
    }
  }
};

const fetchLabProductsData = async () => {
  const queryParams = getQueryParams();
  queryParams.set('stone_type', 'lab');
  const signal = activeAbortController.signal;

  try {
    labProducts.value = [];
    labCurrentIndex.value = 0;
    labBufferedItems.value = [];
    labTotalMaxItems.value = 0;

    const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}?${queryParams.toString()}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      return;
    }
    const data: CatalogResponse = await response.json();

    labTotalMaxItems.value = data.request.max;
    await fetchItemsBatch(maxLoadMoreItems, 'lab');
  } catch (e) {
    if (!signal.aborted) {
      console.error("Failed to fetch lab products", e);
    }
  }
}

const fetchProducts = async () => {
  const queryParams = getQueryParams();
  const currentParams = `${props.catalogItemType}?${queryParams.toString()}`;

  if (currentParams === lastFetchedParams.value) return;
  lastFetchedParams.value = currentParams;

  // Cancel any requests still in flight from a previous, now-superseded generation.
  activeAbortController.abort();
  activeAbortController = new AbortController();
  const signal = activeAbortController.signal;
  individualItemCache.clear();
  multipleItemsCache.clear();

  try {
    loading.value = true;
    emit('update:loading', true);
    error.value = null;
    emit('update:error', null);

    products.value = [];
    failedProducts.value = [];
    usedHashes.clear();
    currentIndex.value = 0;
    totalMaxItems.value = 0;
    bufferedItems.value = [];
    failedProductsExpanded.value = false;

    labProducts.value = [];
    labCurrentIndex.value = 0;
    labBufferedItems.value = [];
    labTotalMaxItems.value = 0;
    loadingMore.value = false;
    labLoadingMore.value = false;
    itemsAddedInCurrentBatch.value = 0;
    labItemsAddedInCurrentBatch.value = 0;

    emit('update:products', []);

    const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}?${queryParams.toString()}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    const data: CatalogResponse = await response.json();

    totalMaxItems.value = data.request.max;
    filters.value = data.filter;
    possibleImagePositions.value = data.images;

    emit('update:filters', filters.value);
    emit('update:possibleImagePositions', possibleImagePositions.value);

    await fetchItemsBatch(maxLoadMoreItems);

    if (showLabProducts.value) {
      await fetchLabProductsData();
    } else {
      labProducts.value = [];
      labTotalMaxItems.value = 0;
    }

  } catch (e) {
    if (signal.aborted) {
      return;
    }
    console.error(e)
    lastFetchedParams.value = '';
    error.value = t(`${pageStr}.load_failed`);
    emit('update:error', error.value);
  } finally {
    if (!signal.aborted) {
      loading.value = false;
      emit('update:loading', false);
    }
  }

  // After the main catalog is loaded, apply initial values for filters (if provided by API)
  const initialFilters = Object.entries(filters.value)
      .map(([key, f]) => [key, (f as any)?.init as string | undefined] as const)
      .filter(([key, initVal]) => typeof initVal === 'string' && initVal !== '' && (!props.activeFilters[key] || props.activeFilters[key] === ''));
  for (const [key, initVal] of initialFilters) {
    console.log(`Applying initial filter ${key}=${initVal}`);
    emit('update-filter', key, initVal as string);
  }


};

const loadMore = () => {
  fetchItemsBatch(maxLoadMoreItems);
};

const loadMoreLab = () => {
  fetchItemsBatch(maxLoadMoreItems, 'lab');
};

const addMarketingBoxLeft = (itemIdx: number) => {
  return itemIdx == 4 || (itemIdx - 4) % 16 == 0
}

const addMarketingBoxRight = (itemIdx: number) => {
  return itemIdx == 13 || (itemIdx - 13) % 16 == 0
}

// Watchers to trigger fetch
watch(() => [props.catalogItemType, props.activeFilters], () => {
  fetchProducts();
}, { deep: true, immediate: true });

const isMinPrice = computed(() => {
  // is price sort min-price (either explicitly selected or auto-applied for wedding rings)
  return props.activeFilters.price_sort === 'min-price' && isMinPriceAllowed.value
})

</script>

<template>
  <section aria-labelledby="products-heading"
           class="sticky top-0 pt-10 h-fit grow lg:w-3/4 bg-gray-100 px-2 ld:px-6 lg:border-r border-gray-200">
    <h2 id="products-heading" class="sr-only">{{ $t(`${pageStr}.products_sr`) }}</h2>

    <div v-if="loading" class="grid grid-cols-2 gap-6 lg:grid-cols-4">
      <CatalogItemSkeleton v-for="i in maxLoadMoreItems" :key="i"/>
    </div>

    <div v-else-if="error" class="flex justify-center items-center min-h-[300px]">
      <div class="text-center">
        <div class="text-red-600 text-base mb-3">{{ error }}</div>
        <button
            class="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            @click="fetchProducts()"
        >
          {{ $t(`${pageStr}.try_again`) }}
        </button>
      </div>
    </div>

    <div v-else class="pb-20">
      <CatalogResultEmpty
          v-if="products.length === 0"
          :active-filters="activeFilters"
          @update:filter="(name, value) => $emit('update-filter', name, value)"
      />

      <div v-if="products.length > 0" class="grid gap-2 grid-cols-2 xl:grid-cols-4 xl:gap-6 gap-y-2">
        <template v-for="(item, index) in products" :key="item.set_id">
          <div v-if="addMarketingBoxLeft(index)" class="col-span-2 row-span-2">
            <CatalogBanner
                class="col-span-2"
                :index="index"
                :item="item"
                :catalog-banners="Config.getCatalogBanners()"
            />
          </div>
          <CatalogItem
              :product="item"
              :index="index"
              :hash="hash"
              :catalog-item-type="catalogItemType"
              :fix-image-position="fixImagePosition"
              :is-min-Price="isMinPrice"
          />
          <div v-if="addMarketingBoxRight(index)" class="col-span-2 row-span-2 flex">
            <CatalogBanner
                class="col-span-2"
                :index="index"
                :item="item"
                :catalog-banners="Config.getCatalogBanners()"
            />
          </div>
        </template>
        <template v-if="isLoadingMore">
          <CatalogItemSkeleton v-for="i in (maxLoadMoreItems - itemsAddedInCurrentBatch)" :key="'skeleton-' + i"/>
        </template>
      </div>

      <div v-if="loadingMore" class="mt-10 flex justify-center uppercase">
        <button
            class="cursor-pointer px-20 py-3 bg-white border border-brand-main text-brand-text hover:bg-brand-main hover:text-white text-base tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
            :disabled="isLoadingMore"
            @click="loadMore">
          <span class="uppercase">{{ $t(`${pageStr}.load_more`) }}</span>
        </button>
      </div>

      <div v-if="labProducts.length > 0" class="mt-12 border-t border-gray-300 pt-12">
        <h3 class="text-base font-light text-center mb-8 uppercase tracking-widest text-gray-500">
          {{ $t(`${pageStr}.further_possibilities_lab`) }}
        </h3>
        <div class="grid gap-6 grid-cols-2 xl:grid-cols-4 gap-y-6">
          <CatalogItem
              v-for="item in labProducts"
              :key="'lab-' + item.set_id"
              :product="item"
              :hash="hash"
              :catalog-item-type="catalogItemType"
              :fix-image-position="fixImagePosition"
          />
          <template v-if="isLoadingMoreLab">
            <CatalogItemSkeleton v-for="i in (maxLoadMoreItems - labItemsAddedInCurrentBatch)" :key="'lab-skeleton-' + i"/>
          </template>
        </div>
        <div v-if="labLoadingMore" class="mt-10 flex justify-center uppercase">
          <button
              class="cursor-pointer px-20 py-3 bg-white border border-brand-main text-brand-text hover:bg-brand-main hover:text-white text-base tracking-widest disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="isLoadingMoreLab"
              @click="loadMoreLab">
            <span class="uppercase">{{ $t(`${pageStr}.load_more`) }}</span>
          </button>
        </div>
      </div>

      <div v-if="failedProducts.length > 0" class="mt-12 border-t border-gray-300 pt-12">
        <div class="flex flex-col items-center">
          <h3 class="text-base font-light text-center mb-4 uppercase tracking-widest text-gray-500">
            {{ $t(`${pageStr}.failed_products_title`) }}
          </h3>
          <button
              @click="failedProductsExpanded = !failedProductsExpanded"
              class="mb-8 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            {{ failedProductsExpanded ? $t(`${pageStr}.hide_failed_products`) : $t(`${pageStr}.show_failed_products`) }}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 transition-transform"
                :class="{'rotate-180': failedProductsExpanded}"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div v-if="failedProductsExpanded" class="grid gap-6 grid-cols-2 xl:grid-cols-4 gap-y-6">
          <CatalogItem
              v-for="item in failedProducts"
              :key="'failed-' + item.set_id"
              :product="item"
              :catalog-item-type="catalogItemType"
              :fix-image-position="fixImagePosition"
          />
        </div>
      </div>
    </div>
  </section>
</template>
